/**
 * useSwipeBack Hook
 *
 * Enables iOS/Android-style edge swipe gesture to navigate back.
 * Detects a right-swipe starting from the left edge of the screen
 * and triggers back navigation (same as tapping the back arrow).
 *
 * Only active on:
 * - Native platforms (iOS/Android via Capacitor)
 * - Sub-pages (where the TopBar back button is visible)
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNative } from '@/lib/capacitor';

// Routes in the bottom navigation (no back gesture on these)
const MAIN_ROUTES = ['/', '/ssw', '/baby', '/tools', '/chat', '/profile'];

// Gesture detection thresholds
const EDGE_ZONE = 30; // pixels from left edge to start tracking
const SWIPE_MIN_DISTANCE = 80; // minimum horizontal distance for valid swipe
const SWIPE_MAX_VERTICAL_RATIO = 0.5; // max vertical/horizontal ratio (keeps swipe horizontal)

export function useSwipeBack() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Only enable on native platforms and sub-pages
    if (!isNative) return;
    if (MAIN_ROUTES.includes(location.pathname)) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      // Only start tracking if touch begins in the left edge zone
      if (touch.clientX <= EDGE_ZONE) {
        startX = touch.clientX;
        startY = touch.clientY;
        tracking = true;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // Valid swipe: sufficient horizontal distance, mostly horizontal direction
      if (dx >= SWIPE_MIN_DISTANCE && dy / dx < SWIPE_MAX_VERTICAL_RATIO) {
        navigate(-1);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [location.pathname, navigate]);
}
